import { Trash2 } from 'lucide-react';
import { InfoTooltip } from './InfoTooltip';
import {
  CONTEXT_SOURCES,
  CONTEXT_SOURCE_ORDER,
  inputLocationForMethod,
  type ContextSource,
  type InputLocation,
  type InputRowsIssue,
  type ToolInputRow,
  type ToolInputType,
} from './tool-inputs';
import styles from './ToolForm.module.css';

const INPUT_TYPES: ToolInputType[] = ['string', 'number', 'boolean'];

interface ToolInputsTableProps {
  rows: ToolInputRow[];
  issues: InputRowsIssue[];
  method: string;
  onChange: (rows: ToolInputRow[]) => void;
}

export const ToolInputsTable = ({ rows, issues, method, onChange }: ToolInputsTableProps) => {
  const issueByIndex = new Map(issues.map(issue => [issue.index, issue.message]));
  // GET has no body, so every field rides in the query string and the location picker is locked.
  const naturalLocation = inputLocationForMethod(method);
  const locationLocked = naturalLocation === 'query';

  const update = (index: number, patch: Partial<ToolInputRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  const remove = (index: number) => onChange(rows.filter((_, i) => i !== index));
  const add = () => onChange([...rows, { name: '', type: 'string', required: false }]);

  // A Reach-sourced field is auto-supplied at request time, so it's auto-required (always present).
  const setSource = (index: number, value: string) =>
    update(index, value ? { source: value as ContextSource, required: true } : { source: undefined });

  return (
    <div className={styles.inputsTable}>
      {rows.length > 0 && (
        <>
          <dl className={styles.columnGuide}>
            <div>
              <dt>Name</dt>
              <dd>We send the value to your API under this exact name.</dd>
            </div>
            <div>
              <dt>Required</dt>
              <dd>
                Tick it and the agent won&apos;t call this tool until it has the value. Leave it off for
                details that are optional. Reach-filled values are always on — we send them with every
                call, so they can&apos;t be made optional.
              </dd>
            </div>
            <div>
              <dt>Source</dt>
              <dd>
                Where the value comes from. Leave it on <em>From the conversation</em> and the agent asks
                the customer for it. Pick a Reach value — like the customer&apos;s phone — and we fill it
                in automatically, so the agent never has to ask.
              </dd>
            </div>
            <div>
              <dt>Location</dt>
              <dd>
                How we send it to your API: in the request <em>Body</em>, or as a <em>Query</em>{' '}
                parameter on the URL.
              </dd>
            </div>
            <div>
              <dt>Description</dt>
              <dd>
                For values from the conversation: a plain-English note telling the agent what belongs
                here — always fill this in. For Reach-filled values the agent never sees this field, so a
                description is just a note for your own reference.
              </dd>
            </div>
          </dl>
          <table className={styles.paramTable}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Required</th>
                <th>Source</th>
                <th>Location</th>
                <th>Description</th>
                <th aria-label="Remove" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => {
                const issue = issueByIndex.get(index);
                return (
                  <tr key={index}>
                    <td>
                      <input
                        type="text"
                        value={row.name}
                        onChange={e => update(index, { name: e.target.value })}
                        className={styles.paramNameInput}
                        placeholder="phone"
                        aria-invalid={Boolean(issue)}
                      />
                      {issue && <span className={styles.paramError}>{issue}</span>}
                    </td>
                    <td>
                      <select
                        value={row.type}
                        onChange={e => update(index, { type: e.target.value as ToolInputType })}
                        className={styles.paramTypeSelect}>
                        {INPUT_TYPES.map(type => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className={styles.paramRequiredCell}>
                      <input
                        type="checkbox"
                        checked={row.source ? true : row.required}
                        disabled={Boolean(row.source)}
                        onChange={e => update(index, { required: e.target.checked })}
                        className={styles.checkbox}
                        aria-label={`${row.name || 'parameter'} required`}
                      />
                      {row.source && (
                        <InfoTooltip label="Why is this always required?">
                          <p>{CONTEXT_SOURCES[row.source].requiredHint}</p>
                        </InfoTooltip>
                      )}
                    </td>
                    <td>
                      <select
                        value={row.source ?? ''}
                        onChange={e => setSource(index, e.target.value)}
                        className={styles.paramSourceSelect}
                        aria-label={`${row.name || 'parameter'} source`}>
                        <option value="">From the conversation</option>
                        <optgroup label="Filled by Reach">
                          {CONTEXT_SOURCE_ORDER.map(source => (
                            <option key={source} value={source}>
                              {CONTEXT_SOURCES[source].label}
                            </option>
                          ))}
                        </optgroup>
                      </select>
                    </td>
                    <td>
                      <select
                        value={locationLocked ? naturalLocation : (row.location ?? 'body')}
                        onChange={e => update(index, { location: e.target.value as InputLocation })}
                        className={styles.paramTypeSelect}
                        disabled={locationLocked}
                        aria-label={`${row.name || 'parameter'} location`}>
                        <option value="body">Body</option>
                        <option value="query">Query</option>
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={row.description ?? ''}
                        onChange={e => update(index, { description: e.target.value })}
                        className={styles.paramDescriptionInput}
                        placeholder={
                          row.source
                            ? 'Optional — Reach fills this in; the agent never sees it'
                            : 'What the agent should put here'
                        }
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className={styles.removeButton}
                        aria-label={`Remove ${row.name || 'parameter'}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
      <button type="button" onClick={add} className={styles.linkButton}>
        + Add parameter
      </button>
    </div>
  );
};
